import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { LOGIN_PAGE_LOGO } from '../../constants';

interface LoginProps {
  /** Retries a fresh B2C redirect after a failed sign-in. */
  onRetry: () => void;
  /** Set only when a sign-in attempt failed; unset while just redirecting. */
  errorMessage?: string;
}

/**
 * The unauthenticated screen — briefly, since there is no sign-in button to
 * click here. `App.tsx` redirects to B2C automatically the moment `status` is
 * `signedOut`, the same way AdminPortal's `MsalAuthenticationTemplate` does,
 * so this only ever renders for the instant before that redirect fires, or
 * when a sign-in attempt has already failed and needs a visible Retry.
 *
 * The legacy Login container carried a username/password form, a forgot
 * password flow, and (through Part 1 of this migration) a manual "Sign in
 * with Microsoft 365" button. All three are gone now: the first two were
 * already unreachable dead code, and the third stopped matching how every
 * other MRO app signs a user in.
 */
const Login = ({ onRetry, errorMessage }: LoginProps) => (
  <Container className='login-page'>
    <Grid
      container
      spacing={3}
      className='login-page-wrapper'
      justifyContent='center'
      alignItems='center'
    >
      <Grid size={{ xs: 12, md: 6, lg: 4 }} textAlign='center'>
        <img
          src={LOGIN_PAGE_LOGO}
          alt='MRO'
          style={{ maxWidth: '280px', marginBottom: '2rem' }}
        />

        {errorMessage ? (
          <Alert
            severity='error'
            action={
              <Button color='inherit' size='small' onClick={onRetry}>
                Retry
              </Button>
            }
          >
            {errorMessage}
          </Alert>
        ) : (
          <Typography
            variant='body2'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={18} />
            Redirecting to sign-in&hellip;
          </Typography>
        )}
      </Grid>
    </Grid>
  </Container>
);

export default Login;
